/**
 * Naam Darbar — Footer Component (Theme-aware)
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const linkCls = 'text-sm transition-colors text-tx-secondary hover:text-accent-500';
const headingCls = 'text-sm font-semibold mb-3 text-tx-primary';

function Footer() {
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';

  return (
    <footer className="mt-auto border-t border-line bg-elevated transition-colors duration-300">
      <div className="container-page py-12">
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-2 md:grid-cols-4">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center mb-3 group w-fit">
              <img
                src={isSukhasan ? '/Dark_theme_brand_text.svg' : '/White_theme_brand_text.svg'}
                alt="Naam Darbar"
                className="h-[22px] w-auto"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-sm max-w-xs text-tx-secondary">
              Discover Sikh Kirtan Ragis and upcoming Samagams near you.
              Forward event messages to our WhatsApp to help grow the community.
            </p>
          </div>

          {/* Navigate — core pages */}
          <div>
            <h3 className={headingCls}>Navigate</h3>
            <ul className="space-y-2">
              <li><Link to="/" className={linkCls}>Home</Link></li>
              <li><Link to="/ragis" className={linkCls}>Browse Ragis</Link></li>
              <li><Link to="/events" className={linkCls}>Upcoming Samagams</Link></li>
              <li><Link to="/gurudwaras" className={linkCls}>Gurudwaras</Link></li>
            </ul>
          </div>

          {/* Discover */}
          <div>
            <h3 className={headingCls}>Discover</h3>
            <ul className="space-y-2">
              <li><Link to="/kirtan-near-me" className={linkCls}>Kirtan Near Me</Link></li>
              <li><Link to="/samagam-near-me" className={linkCls}>Samagam Near Me</Link></li>
              <li><Link to="/akj-samagam" className={linkCls}>AKJ Samagam</Link></li>
              <li><Link to="/book-ragi" className={linkCls}>Book a Ragi</Link></li>
              <li><Link to="/amrit-sanchar" className={linkCls}>Amrit Sanchar</Link></li>
              <li><Link to="/faq" className={linkCls}>FAQ</Link></li>
            </ul>
          </div>

          {/* Popular Cities for SEO internal linking */}
          <div>
            <Link to="/kirtan-by-city" className={`${headingCls} block hover:underline`}>
              Kirtan by City
            </Link>
            <ul className="space-y-2">
              {[
                { name: 'Amritsar', slug: 'amritsar' },
                { name: 'Delhi', slug: 'delhi' },
                { name: 'Chandigarh', slug: 'chandigarh' },
                { name: 'Ludhiana', slug: 'ludhiana' },
                { name: 'Mumbai', slug: 'mumbai' },
                { name: 'Bangalore', slug: 'bangalore' },
              ].map((city) => (
                <li key={city.slug}>
                  <Link to={`/kirtan/${city.slug}`} className={linkCls}>
                    Kirtan in {city.name}
                  </Link>
                </li>
              ))}
              {[
                { name: 'Delhi', slug: 'delhi' },
                { name: 'Amritsar', slug: 'amritsar' },
                { name: 'Ludhiana', slug: 'ludhiana' },
              ].map((city) => (
                <li key={`samagam-${city.slug}`}>
                  <Link to={`/samagam/${city.slug}`} className={linkCls}>
                    {city.name} Samagam
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 border-t border-line pt-6 flex flex-col items-center gap-2 text-center">
          <p className="text-sm font-medium tracking-wide text-accent-500">
            ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖ਼ਾਲਸਾ, ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫ਼ਤਹਿ
          </p>
          <p className="text-xs text-tx-muted">
            © {new Date().getFullYear()} Naam Darbar. Built with ❤️ for the Sikh Sangat.
          </p>
          <div className="flex items-center gap-3">
            <Link to="/privacy-policy" className="text-xs transition-colors text-tx-muted hover:text-accent-500">
              Privacy Policy
            </Link>
            <span className="text-xs text-tx-muted">·</span>
            <Link to="/terms-of-service" className="text-xs transition-colors text-tx-muted hover:text-accent-500">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Memo — no props; skips the re-render Layout triggers on every route
// change / auth update. Theme changes still propagate via useTheme.
export default memo(Footer);
